import { v4 as uuidv4 } from 'uuid'
import configs from '@/config'
import cookie from '@/services/cookie'
import { UNLEASH_SESSION_COOKIE } from './unleashSession.constants'

// Re-export so existing importers of `UNLEASH_SESSION_COOKIE` from this module
// keep working; the canonical definition lives in the dependency-free
// constants module (one source of truth, shared with unleash.js).
export { UNLEASH_SESSION_COOKIE }

const TWO_YEARS_IN_DAYS = 730

// Opaque-token guard — mirrors general-api's resolveSessionId. We only bound
// charset (no control chars → no header/log injection) and length; the exact
// shape is irrelevant to Unleash hashing.
const SESSION_ID_REGEX = /^[\w-]{1,64}$/
export const isValidSessionId = (value) => typeof value === 'string' && SESSION_ID_REGEX.test(value)

// Read the anchor with the RAW key — must stay symmetric with the write path
// (cookie.setRootDomainCookie writes the raw key, no transformKey) and with
// unleash.js readSessionCookie. cookie.getValue() would apply transformKey and
// read `eu-aha_unleash_session` in the EU region, so it could never find the
// raw cookie this function (and aha-pricing) writes — re-minting on every load.
export const readRawSessionCookie = () => {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(new RegExp(`(?:^|; )${UNLEASH_SESSION_COOKIE}=([^;]*)`))
  if (!match) return undefined
  // decodeURIComponent throws URIError on a malformed cookie; fall back to the
  // raw value so a bad cookie can never crash bootstrap.
  try {
    return decodeURIComponent(match[1])
  } catch (error) {
    return match[1]
  }
}

/**
 * Read the shared session cookie; mint + persist one if absent or malformed.
 * READ-THEN-MINT — never overwrites a valid existing value, so visit order
 * (presenter-first vs pricing-first) can't flip the variant.
 *
 * The cookie is written on the environment-driven ROOT domain via
 * cookie.setRootDomainCookie (localhost / ahaslide.com / ahaslides.com), never
 * a hardcoded domain. `Secure` is off only on local (http://localhost); on/off
 * tracks https so the browser actually stores it.
 *
 * @returns {string} the resolved session id
 */
export const ensureUnleashSession = () => {
  const existing = readRawSessionCookie()
  if (isValidSessionId(existing)) return existing

  const minted = uuidv4()
  cookie.setRootDomainCookie(UNLEASH_SESSION_COOKIE, minted, {
    expires: TWO_YEARS_IN_DAYS,
    sameSite: 'Lax',
    secure: configs.env !== 'local',
  })
  return minted
}
